import React, { useState } from 'react';
import { Card, Typography, Row, Col, Select, Button, Alert, Divider } from 'antd';
import { IoSwapHorizontalOutline } from 'react-icons/io5';
import { toast } from 'react-hot-toast';
import { User, UserRole } from '@/types';

const { Title } = Typography;

interface UserImpersonationCardProps {
  companyAdmins: User[];
  analyticsUsers: User[];
  customerUsers: User[];
  onImpersonate: (email: string) => Promise<void>;
}

export const UserImpersonationCard: React.FC<UserImpersonationCardProps> = ({
  companyAdmins,
  analyticsUsers,
  customerUsers,
  onImpersonate,
}) => {
  const [selectedRole, setSelectedRole] = useState<UserRole | ''>('');
  const [selectedUserId, setSelectedUserId] = useState('');

  const getUsersForRole = () => {
    if (selectedRole === UserRole.CompanyAdmin) return companyAdmins;
    if (selectedRole === UserRole.Analytics) return analyticsUsers;
    if (selectedRole === UserRole.Customer) return customerUsers;
    return [];
  };

  const handleImpersonate = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user to impersonate');
      return;
    }

    const allUsers = [...companyAdmins, ...analyticsUsers, ...customerUsers];
    const selectedUser = allUsers.find(u => u.id === selectedUserId);

    if (!selectedUser) {
      toast.error('User not found');
      return;
    }

    await onImpersonate(selectedUser.email);

    // Reset selections
    setSelectedRole('');
    setSelectedUserId('');
  };

  return (
    <Card>
      <Title level={4} className="flex items-center gap-2 mb-4">
        <IoSwapHorizontalOutline /> User Impersonation
      </Title>
      <Divider className="mt-0" />

      <Alert
        description="Switch between different users to test role-based access control and regional filtering. This allows you to verify that the correct data is shown for each user type."
        type="info"
        showIcon
        className="mb-6"
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Select
            className="w-full"
            placeholder="Select Role"
            value={selectedRole}
            onChange={(value) => {
              setSelectedRole(value);
              setSelectedUserId('');
            }}
            options={[
              { value: '', label: '-- Select Role --' },
              { value: UserRole.CompanyAdmin, label: `Company Admin (${companyAdmins.length} users)` },
              { value: UserRole.Analytics, label: `Analytics (${analyticsUsers.length} users)` },
              { value: UserRole.Customer, label: `Customer (${customerUsers.length} users)` },
            ]}
          />
        </Col>

        <Col xs={24} md={12}>
          <Select
            className="w-full"
            placeholder="Select User"
            value={selectedUserId}
            onChange={setSelectedUserId}
            disabled={!selectedRole}
            options={[
              { value: '', label: '-- Select User --' },
              ...getUsersForRole().map(u => ({
                value: u.id,
                label: `${u.name} ${u.surname} - ${u.email}${u.assignedRegions.length > 0 ? ` (${u.assignedRegions.join(', ')})` : ''}${u.installerCompanyName ? ` - ${u.installerCompanyName}` : ''}`,
              })),
            ]}
          />
        </Col>

        <Col xs={24} md={4}>
          <Button
            type="primary"
            className="w-full h-full"
            icon={<IoSwapHorizontalOutline />}
            onClick={handleImpersonate}
            disabled={!selectedUserId}
          >
            Switch User
          </Button>
        </Col>
      </Row>
    </Card>
  );
};
